import type { HomeyAPI, Image } from 'homey-api';

import { __DEV__ } from '../../lib/__dev__';
import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type ImageProps = {
  [K in keyof Image as Image[K] extends Function ? never : K]: Image[K];
};

type ImagesData = { [key: string]: Image };
type ImagesById = { [key: string]: ImageProps };

interface State {
  data: null | ImagesData;
  byId: null | ImagesById;
  isLoading: boolean;
  error: unknown;
}

export type ImagesStoreState = State;

export class ImagesStore extends BaseApiStore<State, Image> {
  constructor() {
    super('images');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.images, reference: this });

      if (abortController.signal.aborted) return;

      const imagesPromise = api.images.getImages({ $cache: cache });
      const [imagesData] = await Promise.all([imagesPromise]);

      if (abortController.signal.aborted) return;

      const images = Object.values(imagesData);

      const data: ImagesData = {};
      const byId: ImagesById = {};

      for (const imageInstance of images) {
        const imageReference = { ...imageInstance };

        data[imageReference.id] = imageInstance;
        byId[imageReference.id] = imageReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.images, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.images.on('image.create', this.handleCreate);
    api.images.on('image.update', this.handleUpdate);
    api.images.on('image.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.images.off('image.create', this.handleCreate);
    api.images.off('image.update', this.handleUpdate);
    api.images.off('image.delete', this.handleDelete);
  }

  getInstanceFromCache(image: { id: string }) {
    return this.getCache()[image.id];
  }

  getCache(): { [key: string]: Image } {
    const api = this.getApi();
    const cache = api.images.__cache['image'];
    return cache;
  }

  handleCreate = (createdImage: Image) => {
    this.DEBUG && console.info(`create:image:${createdImage.id}`);
    const state = this.get();
    const imageInstance = this.getInstanceFromCache(createdImage);

    if (imageInstance == null) return;

    const imageReference = { ...imageInstance };

    this.set({
      data: {
        ...state.data,
        [createdImage.id]: imageInstance,
      },
      byId: {
        ...state.byId,
        [createdImage.id]: imageReference,
      },
    });
  };

  handleUpdate = (updatedImage: Image) => {
    this.DEBUG && console.info(`update:image:${updatedImage.id}`);

    this.addToQueue(updatedImage.id, updatedImage);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedImage] of Object.entries(updateQueue)) {
        const imageInstance = this.getInstanceFromCache(queuedImage);

        if (state.data == null) {
          this.DEBUG && console.info(`update:image:missing-data`);
          continue;
        }

        if (imageInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:image:unknown:${id}`);
          continue;
        }

        const imageReference = { ...imageInstance };

        nextById[id] = imageReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:image:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedImage: Image) => {
    this.DEBUG && console.info(`delete:image:${deletedImage.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:image:missing-data`);
      return;
    }

    const imageInstance = state.data[deletedImage.id];

    if (imageInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[imageInstance.id];
    delete nextById[imageInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const images = Object.values(cache ?? {});
    const data: ImagesData = {};
    const byId: ImagesById = {};

    for (const imageInstance of images) {
      const imageReference = { ...imageInstance };
      data[imageReference.id] = imageInstance;
      byId[imageReference.id] = imageReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const imagesStore = new ImagesStore();
